import { FileText, FlaskConical, LucideIcon, UsersIcon } from 'lucide-react';

import { isTestingToolsUiEnabled } from '~/lib/testing-tools';

export const routes = {
  home: '/',
  login: '/login',
  forgot: '/forgot',
  reset: '/reset',
  dashboard: '/dashboard',
  projects: '/dashboard',
  newProject: '/dashboard/projects/new',
  editProject: (projectId: string) => `/dashboard/projects/${projectId}/edit`,
  clockings: '/dashboard/logs',
  logs: '/dashboard/logs',
  newClocking: '/dashboard/logs/new',
  editClocking: (clockingId: string) => `/dashboard/logs/${clockingId}/edit`,
  profile: '/dashboard/profile',
  register: '/register',
  verify: '/verify',
  users: '/dashboard/users',
  usersWithUserId: (userId: number | string) =>
    `/dashboard/users?userId=${encodeURIComponent(String(userId))}`,
  profileComparison: '/dashboard/profile-comparison',
  generateMatch: '/dashboard/generate-match',
  searchAndMatch: '/dashboard/search-and-match',
  nonVerifiedUsers: '/dashboard/non-verified-users',
  moderationRejectedUsers: '/dashboard/moderation-rejected-users',
  moderationManualReviewUsers: '/dashboard/moderation-manual-review-users',
  validationFailedUsers: '/dashboard/validation-failed-users',
  validationManualReviewUsers: '/dashboard/validation-manual-review-users',
  premiumPendingMatches: '/dashboard/pending-matches',
  assistantConfig: '/dashboard/ai-config',
  moderationRejectionHistory: '/dashboard/history/moderation-rejection',
  moderationStatistics: '/dashboard/statistics/moderation',
  verificationStatistics: '/dashboard/statistics/verification',
  publishingArticles: '/dashboard/publishing/articles',
  publishingMatchmakers: '/dashboard/publishing/matchmakers',
  testingUserChats: '/dashboard/testing-tools/user-chats'
};

export interface NavItem {
  titleId: string
  url: string
  disabled?: boolean
  external?: boolean
  shortcut?: [string, string]
  icon?: LucideIcon
  label?: string
  description?: string
  isActive?: boolean
  items?: NavItem[]
}

export interface NavItemWithChildren extends NavItem {
  items: NavItemWithChildren[]
}

export const navItems: NavItem[] = [
  {
    titleId: 'users',
    url: routes.users,
    icon: UsersIcon,
    isActive: false,
    shortcut: ['u', 'u'],
    items: [] // Empty array as there are no child items for Users
  },
  {
    titleId: 'profileComparison',
    url: routes.profileComparison,
    icon: UsersIcon,
    isActive: true,
    shortcut: ['u', 'u'],
    items: [] // Empty array as there are no child items for profileComparison
  },
  {
    titleId: 'searchAndMatch',
    url: routes.searchAndMatch,
    icon: UsersIcon,
    isActive: true,
    shortcut: ['s', 'm'],
    items: []
  },
  {
    titleId: 'generateMatch',
    url: routes.generateMatch,
    icon: UsersIcon,
    isActive: true,
    shortcut: ['u', 'u'],
    items: [] // Empty array as there are no child items for profileComparison
  },
  {
    titleId: 'assistantConfigurations',
    url: routes.assistantConfig,
    icon: UsersIcon,
    isActive: true,
    shortcut: ['u', 'u'],
    items: [] // Empty array as there are no child items for profileComparison
  },
  // {
  //   titleId: 'logs',
  //   url: routes.logs,
  //   icon: LucideShoppingBag,
  //   shortcut: ['l', 'l'],
  //   isActive: false,
  //   items: [] // No child items
  // },
  {
    titleId: 'history',
    url: '/dashboard/history',
    icon: UsersIcon,
    isActive: false,
    shortcut: ['h', 'h'],
    items: [
      {
        titleId: 'moderationRejectionHistoryPage',
        url: routes.moderationRejectionHistory,
        isActive: false,
        shortcut: ['h', 'm'],
        items: []
      }
    ]
  },
  {
    titleId: 'incidents',
    url: '#', // parent container, not navigable
    icon: UsersIcon,
    isActive: false,
    shortcut: ['i', 'i'],
    items: [
      {
        titleId: 'nonVerifiedUsers',
        url: routes.nonVerifiedUsers,
        isActive: false,
        shortcut: ['n', 'v'],
        items: []
      },
      {
        titleId: 'moderationRejectedUsers',
        url: routes.moderationRejectedUsers,
        isActive: false,
        shortcut: ['m', 'r'],
        items: []
      },
      {
        titleId: 'moderationManualReviewUsers',
        url: routes.moderationManualReviewUsers,
        isActive: false,
        shortcut: ['m', 'm'],
        items: []
      },
      {
        titleId: 'validationFailedUsers',
        url: routes.validationFailedUsers,
        isActive: false,
        shortcut: ['v', 'f'],
        items: []
      },
      {
        titleId: 'validationManualReviewUsers',
        url: routes.validationManualReviewUsers,
        isActive: false,
        shortcut: ['v', 'm'],
        items: []
      },
      {
        titleId: 'premiumPendingMatches',
        url: routes.premiumPendingMatches,
        isActive: false,
        shortcut: ['p', 'm'],
        items: []
      }
    ]
  },
  {
    titleId: 'statistics',
    url: '/dashboard/statistics',
    icon: UsersIcon,
    isActive: false,
    shortcut: ['s', 's'],
    items: [
      {
        titleId: 'moderationStatisticsPage',
        url: routes.moderationStatistics,
        isActive: false,
        shortcut: ['s', 'm'],
        items: []
      },
      {
        titleId: 'verificationStatisticsPage',
        url: routes.verificationStatistics,
        isActive: false,
        shortcut: ['s', 'v'],
        items: []
      }
    ]
  },
  {
    titleId: 'publishing',
    url: '/dashboard/publishing',
    icon: FileText,
    isActive: false,
    shortcut: ['e', 'd'],
    items: [
      {
        titleId: 'publishingArticles',
        url: routes.publishingArticles,
        isActive: false,
        shortcut: ['e', 'a'],
        items: []
      },
      {
        titleId: 'publishingMatchmakers',
        url: routes.publishingMatchmakers,
        isActive: false,
        shortcut: ['e', 'm'],
        items: []
      }
    ]
  },
  ...(isTestingToolsUiEnabled()
    ? [
        {
          titleId: 'testingTools',
          url: '/dashboard/testing-tools',
          icon: FlaskConical,
          isActive: false,
          shortcut: ['t', 't'] as [string, string],
          items: [
            {
              titleId: 'userChats',
              url: routes.testingUserChats,
              isActive: false,
              shortcut: ['t', 'c'] as [string, string],
              items: []
            }
          ]
        } satisfies NavItem
      ]
    : [])
];

export const AUTH_ROUTES = [
  routes.login,
  routes.register,
  routes.verify,
  routes.forgot,
  routes.reset
];
